import React, { useState } from 'react';
import { Loader2, Brain, AlertCircle, RotateCcw } from 'lucide-react';

const StatusLine = ({ pendingCount, gradingStatus }) => {
  if (gradingStatus?.gradeable > 0 && gradingStatus?.graded > 0) {
    return `Grading in progress — ${gradingStatus.graded} graded, ${gradingStatus.gradeable} remaining`;
  }
  if (gradingStatus?.gradeable > 0) {
    return `${gradingStatus.gradeable} calls ready to grade (${gradingStatus.gradeable_has_transcript || 0} have transcript, ${gradingStatus.gradeable_needs_fetch || 0} need transcription fetch)`;
  }
  return `${pendingCount} calls awaiting grading`;
};

// Translate raw provider/stack errors into plain English a manager can act
// on. Raw text stays available behind a "technical details" disclosure.
const friendlyError = (raw) => {
  const s = String(raw || '').toLowerCase();
  if (s.includes('rate limit') || s.includes('429') || s.includes('too many requests'))
    return 'The AI provider was busy (rate-limited). Retrying usually fixes this.';
  if (s.includes('overloaded') || s.includes('529') || s.includes('503') || s.includes('502') || s.includes('unavailable'))
    return 'The AI provider was temporarily overloaded. Retrying usually fixes this.';
  if (s.includes('timeout') || s.includes('timed out') || s.includes('deadline'))
    return 'The AI took too long to respond. Retrying usually fixes this.';
  if (s.includes('json') || s.includes('parse') || s.includes('unexpected token'))
    return 'The AI sent back a malformed response. A retry normally resolves it.';
  if (s.includes('api key') || s.includes('authentication') || s.includes('401') || s.includes('unauthorized'))
    return 'The AI provider rejected our credentials — contact your administrator.';
  if (s.includes('budget') || s.includes('quota') || s.includes('insufficient') || s.includes('billing'))
    return 'The AI budget or quota was exhausted at the time — check the LLM budget settings.';
  if (s.includes('transcript'))
    return 'No usable transcript was available for this call.';
  if (s.includes('connect') || s.includes('network') || s.includes('dns') || s.includes('reset'))
    return 'A network hiccup interrupted the grading. Retrying usually fixes this.';
  return 'An unexpected error interrupted grading. A retry normally resolves it.';
};

const GradingErrorPanel = ({ gradingStatus, onRetryFailed }) => {
  const [retrying, setRetrying] = useState(false);
  return (
    <div className="bg-red-500/10 border border-red-500/20 rounded-xl px-5 py-3" data-testid="grading-error-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-red-400 text-sm font-medium flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {gradingStatus.error_count} call{gradingStatus.error_count !== 1 ? 's' : ''} failed to grade
        </p>
        {onRetryFailed && (
          <button
            onClick={async () => { setRetrying(true); try { await onRetryFailed(); } finally { setRetrying(false); } }}
            disabled={retrying}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/20 hover:bg-red-500/30 text-red-300 text-xs font-semibold rounded-lg transition-colors disabled:opacity-50"
            data-testid="retry-failed-gradings-btn"
          >
            {retrying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
            Retry all failed
          </button>
        )}
      </div>
      {gradingStatus.recent_errors?.length > 0 && (
        <div className="mt-2 space-y-1.5">
          {gradingStatus.recent_errors.map(e => (
            <div key={e.call_id || e.error} data-testid="grading-error-row">
              <p className="text-red-300/80 text-xs">
                • {friendlyError(e.error)}
                <span className="text-red-400/40 ml-1">
                  (call {e.call_id?.slice(0, 10)}…, {e.attempts} attempt{e.attempts !== 1 ? 's' : ''})
                </span>
              </p>
            </div>
          ))}
          <details className="mt-1">
            <summary className="text-red-400/40 text-[10px] cursor-pointer hover:text-red-400/70">Technical details</summary>
            <div className="mt-1 space-y-1">
              {gradingStatus.recent_errors.map(e => (
                <p key={`raw-${e.call_id || e.error}`} className="text-red-400/40 text-[10px] font-mono break-all">
                  {e.call_id}: {e.error}
                </p>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
};

export const GradingBanner = ({ pendingCount, gradingStatus, grading, onGradePending, onRetryFailed }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between bg-amber-500/10 border border-amber-500/20 rounded-xl px-5 py-3">
      <div>
        <p className="text-amber-300 text-sm font-medium">
          <StatusLine pendingCount={pendingCount} gradingStatus={gradingStatus} />
        </p>
        <p className="text-amber-300/50 text-xs">
          {gradingStatus?.graded_pct
            ? `${gradingStatus.graded_pct}% complete — page auto-refreshes when done`
            : 'Click "Grade All Pending" to fetch transcriptions and grade calls'}
        </p>
      </div>
      <button
        onClick={onGradePending}
        disabled={grading}
        className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-400 text-white text-sm font-medium rounded-lg transition-colors"
        data-testid="grade-pending-btn"
      >
        {grading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Brain className="w-4 h-4" />}
        Grade All Pending
      </button>
    </div>

    {gradingStatus?.error_count > 0 && <GradingErrorPanel gradingStatus={gradingStatus} onRetryFailed={onRetryFailed} />}

    {gradingStatus?.no_transcript > 0 && (
      <div className="bg-slate-500/10 border border-slate-500/20 rounded-xl px-5 py-3">
        <p className="text-slate-400 text-sm">
          {gradingStatus.no_transcript} call{gradingStatus.no_transcript !== 1 ? 's' : ''} have no transcription available — these cannot be graded.
        </p>
      </div>
    )}
  </div>
);
